import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

/** Landing page for password-reset magic links: /reset-password?token=… */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: pw,
      });
      setDone(true);
      toast.success("Password updated. Signing you in…");
      const dest =
        data?.role === "owner" ? "/dashboard" : data?.role === "customer" ? "/shop" : "/app";
      setTimeout(() => nav(dest, { replace: true }), 900);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-sm text-center space-y-3">
          <h1 className="font-heading text-2xl font-bold">Missing reset token</h1>
          <p className="text-sm text-slate-500">
            This page needs a valid magic link. Head back to sign-in and
            request a new one.
          </p>
          <Link to="/login" className="text-[#0055FF] text-sm hover:underline">
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:block relative bg-[#050914] overflow-hidden">
        <div className="absolute inset-0 grid-paper-dark opacity-70" />
        <div
          className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(0,85,255,0.3), transparent 70%)",
          }}
        />
        <div className="relative p-10 h-full flex flex-col text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0055FF] grid place-items-center">
              <PhoneCall className="w-4 h-4" />
            </div>
            <span className="font-heading font-bold text-lg">DialFlow</span>
          </Link>
          <div className="mt-auto max-w-md">
            <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
              Reset password
            </div>
            <h2 className="font-heading text-3xl mt-3 leading-tight">
              Choose a new password to jump back into your dashboard.
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-5"
          data-testid="reset-form"
        >
          <div>
            <div className="w-10 h-10 rounded-md bg-[#0055FF]/10 grid place-items-center mb-3">
              <Lock className="w-5 h-5 text-[#0055FF]" />
            </div>
            <h1 className="font-heading text-2xl font-bold">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">
              Minimum 8 characters. You&apos;ll be signed in automatically.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pw">New password</Label>
            <Input
              id="pw"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={8}
              autoFocus
              data-testid="reset-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw2">Confirm new password</Label>
            <Input
              id="pw2"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              data-testid="reset-password-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || done}
            className="w-full bg-[#0055FF] hover:bg-[#0040CC] h-11"
            data-testid="reset-submit"
          >
            {done ? "Done — redirecting…" : loading ? "Updating…" : "Update password"}
          </Button>
          <p className="text-xs text-slate-400 text-center">
            <Link to="/login" className="hover:underline">
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
